import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { SPOT_CARDS, CHAOS_CARDS, TROPHY_CARD, LEGENDARY_CARD } from "../../lib/assets";
import { ZoomIn, ArrowRight } from "lucide-react";

const Category = ({ eyebrow, title, subtitle, accent, children }) => (
  <div className="mb-6 md:mb-10">
    <p className="eyebrow mb-3" style={{ color: accent }}>
      {eyebrow}
    </p>
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 md:gap-8">
      <h3
        className="font-display font-black leading-[0.95] tracking-tighter"
        style={{ fontSize: "clamp(1.75rem, 4.5vw, 3.25rem)", color: "var(--pw-navy)" }}
      >
        {title}
      </h3>
      {subtitle && (
        <p className="text-[color:var(--pw-navy)]/70 max-w-md leading-relaxed">{subtitle}</p>
      )}
    </div>
    {children}
  </div>
);

const CardTile = ({ card, onClick, badge, badgeColor, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="group text-left card-tilt block w-full"
  >
    <div className="relative rounded-2xl overflow-hidden bg-white aspect-[4/5] md:aspect-[3/4.4] border border-black/5 shadow-lg">
      <img
        src={card.src}
        alt={card.label}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      <div
        className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: "linear-gradient(to top, rgba(14,26,43,0.85), transparent 55%)" }}
      >
        <span className="text-white text-xs uppercase tracking-widest flex items-center gap-2 font-semibold">
          <ZoomIn className="w-4 h-4" /> Enlarge
        </span>
      </div>
    </div>
    <div className="mt-3 md:mt-4 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        {badge && (
          <span
            className="inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: badgeColor }}
          >
            {badge}
          </span>
        )}
        <p
          className="font-display text-lg md:text-xl font-bold mt-2 tracking-tight"
          style={{ color: "var(--pw-navy)" }}
        >
          {card.label}
        </p>
        {card.prompt && (
          <p className="text-xs md:text-sm text-[color:var(--pw-navy)]/60 mt-1 leading-relaxed">
            {card.prompt}
          </p>
        )}
      </div>
    </div>
  </button>
);

export const TheCards = () => {
  const [openCard, setOpenCard] = useState(null);
  const carouselRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Track which observation card is currently snapped into view
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const cards = el.querySelectorAll("[data-carousel-item]");
        const midX = el.scrollLeft + el.clientWidth / 2;
        let best = 0;
        let min = Infinity;
        cards.forEach((c, i) => {
          const cMid = c.offsetLeft + c.offsetWidth / 2;
          const d = Math.abs(cMid - midX);
          if (d < min) { min = d; best = i; }
        });
        setActiveIdx(best);
        raf = null;
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelectorAll("[data-carousel-item]")[i];
    if (card) el.scrollTo({ left: card.offsetLeft - 20, behavior: "smooth" });
  };
  return (
    <section
      id="the-cards"
      data-testid="the-cards-section"
      className="py-10 md:py-28 bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="mb-8 md:mb-20 max-w-3xl">
          <p className="eyebrow mb-4">The Cards</p>
          <h2
            className="font-display font-black leading-[0.95] tracking-tighter"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)", color: "var(--pw-navy)" }}
          >
            A small deck.
            <br />
            An <em style={{ color: "var(--pw-orange)" }}>enormous</em> world.
          </h2>
          <p className="mt-5 text-[color:var(--pw-navy)]/70 max-w-lg leading-relaxed">
            A quick look at the personality of the cards. Final production artwork subject to change.
          </p>
        </div>

        {/* Observation Cards — mobile: snap-scroll carousel with peek; desktop: 4-col grid */}
        <Category
          eyebrow="Observation Cards"
          title="The people you spot."
          subtitle="The core of the deck. Prompts that turn strangers into points."
          accent="var(--pw-purple)"
        >
          {/* Mobile carousel */}
          <div
            ref={carouselRef}
            data-testid="observation-cards-mobile"
            className="lg:hidden -mx-5 px-5 mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ scrollPaddingLeft: "1.25rem" }}
          >
            {SPOT_CARDS.map((c) => (
              <div
                key={c.key}
                data-carousel-item
                className="snap-start shrink-0 w-[86%] max-w-[360px]"
              >
                <CardTile
                  card={c}
                  onClick={() => setOpenCard(c)}
                  badge={c.rarity === "RARE" ? "Rare" : c.rarity === "UNCOMMON" ? "Uncommon" : "Common"}
                  badgeColor={c.rarity === "RARE" ? "var(--pw-purple)" : "var(--pw-blue)"}
                  testId={`observation-card-${c.key}`}
                />
              </div>
            ))}
            <div className="shrink-0 w-2" aria-hidden="true" />
          </div>

          {/* Pagination dots + swipe cue (mobile only) */}
          <div className="lg:hidden mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2" role="tablist" aria-label="Observation cards">
              {SPOT_CARDS.map((c, i) => (
                <button
                  key={c.key}
                  data-testid={`observation-dot-${i}`}
                  onClick={() => goTo(i)}
                  aria-label={`Show ${c.label}`}
                  aria-selected={activeIdx === i}
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: activeIdx === i ? "20px" : "8px",
                    backgroundColor: activeIdx === i ? "var(--pw-navy)" : "rgba(14,26,43,0.25)",
                  }}
                />
              ))}
            </div>
            <p
              data-testid="observation-swipe-cue"
              className="text-[11px] tracking-widest uppercase font-semibold flex items-center gap-1"
              style={{ color: "rgba(14,26,43,0.55)" }}
            >
              Swipe <ArrowRight className="w-3 h-3" />
            </p>
          </div>

          {/* Desktop grid */}
          <div className="hidden lg:grid grid-cols-4 gap-6 mt-6">
            {SPOT_CARDS.map((c) => (
              <CardTile
                key={c.key}
                card={c}
                onClick={() => setOpenCard(c)}
                badge={c.rarity === "RARE" ? "Rare" : c.rarity === "UNCOMMON" ? "Uncommon" : "Common"}
                badgeColor={c.rarity === "RARE" ? "var(--pw-purple)" : "var(--pw-blue)"}
                testId={`observation-card-desktop-${c.key}`}
              />
            ))}
          </div>
        </Category>

        <div className="my-6 md:my-20 h-px bg-black/10" />

        {/* Chaos Cards — mobile: snap-scroll carousel, desktop: 3-col grid */}
        <Category
          eyebrow="Chaos Cards"
          title="The interference."
          subtitle="Special cards change the game — swap hands, double the damage or bin a card. Five in every deck."
          accent="var(--pw-orange)"
        >
          {/* Mobile carousel */}
          <div
            data-testid="chaos-cards-mobile"
            className="lg:hidden -mx-5 px-5 mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            style={{ scrollPaddingLeft: "1.25rem" }}
          >
            {CHAOS_CARDS.map((c) => (
              <div
                key={c.key}
                data-carousel-item
                className="snap-start shrink-0 w-[70%] max-w-[280px]"
              >
                <CardTile
                  card={c}
                  onClick={() => setOpenCard(c)}
                  badge="Chaos"
                  badgeColor="var(--pw-orange)"
                  testId={`chaos-card-${c.key}`}
                />
              </div>
            ))}
            <div className="shrink-0 w-2" aria-hidden="true" />
          </div>

          {/* Desktop grid */}
          <div className="hidden lg:grid grid-cols-3 gap-6 mt-6 max-w-4xl">
            {CHAOS_CARDS.map((c) => (
              <CardTile
                key={c.key}
                card={c}
                onClick={() => setOpenCard(c)}
                badge="Chaos"
                badgeColor="var(--pw-orange)"
                testId={`chaos-card-desktop-${c.key}`}
              />
            ))}
          </div>
        </Category>

        <div className="my-6 md:my-20 h-px bg-black/10" />

        {/* Trophy */}
        <Category
          eyebrow="The Trophy"
          title="The one everyone chases."
          subtitle="Spot something genuinely unusual, bizarre or extraordinary. Claim The Trophy."
          accent="var(--pw-yellow)"
        >
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-[240px] sm:max-w-none mx-auto sm:mx-0">
            <CardTile
              card={TROPHY_CARD}
              onClick={() => setOpenCard(TROPHY_CARD)}
              badge="Trophy"
              badgeColor="var(--pw-yellow)"
              testId={`trophy-card-${TROPHY_CARD.key}`}
            />
          </div>
        </Category>

        <div className="my-6 md:my-20 h-px bg-black/10" />

        {/* Legendary — the rarest card in the deck */}
        <Category
          eyebrow="The Legendary"
          title="One in the entire deck."
          subtitle="A single, mythical card. Spot it in the wild and the game is yours."
          accent="var(--pw-pink)"
        >
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-[240px] sm:max-w-none mx-auto sm:mx-0">
            <CardTile
              card={LEGENDARY_CARD}
              onClick={() => setOpenCard(LEGENDARY_CARD)}
              badge="Legendary"
              badgeColor="var(--pw-pink)"
              testId={`legendary-card-${LEGENDARY_CARD.key}`}
            />
          </div>
        </Category>
      </div>

      <Dialog open={!!openCard} onOpenChange={(v) => !v && setOpenCard(null)}>
        <DialogContent className="max-w-2xl p-0 border-none bg-transparent shadow-none" data-testid="card-lightbox">
          <DialogTitle className="sr-only">{openCard?.label || "Card"}</DialogTitle>
          {openCard && (
            <div className="rounded-2xl overflow-hidden bg-black">
              <img src={openCard.src} alt={openCard.label} className="w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TheCards;
